/**
 * useSeo — central place that
 *   1. fetches /api/public/seo (cached, in-memory)
 *   2. exposes helpers to set page <title>, <meta description>, OG, etc.
 *   3. injects a global LocalBusiness JSON-LD into <head>
 *
 * Per-page usage:
 *   const { setSeo } = useSeo()
 *   setSeo({
 *     title: 'Курстары — Көркем Авто',
 *     description: '...'
 *   })
 */
import { ref } from 'vue'
import api from '@/utils/api'

const seoData = ref(null)
const isLoading = ref(false)
let pendingRequest = null

const SITE_URL = 'https://korkemauto.kz'

const normalizeLocale = (l) => (l === 'kz' || l === 'en' ? l : 'ru')
const localized = (val, locale, fallback = '') => {
  if (!val) return fallback
  const lc = normalizeLocale(locale)
  return val[lc] || val.kz || val.ru || val.en || fallback
}

const loadSeoData = async () => {
  if (seoData.value) return seoData.value
  if (pendingRequest) return pendingRequest
  isLoading.value = true
  pendingRequest = api
    .get('/public/seo')
    .then((response) => {
      seoData.value = response.data || {}
      return seoData.value
    })
    .catch(() => { seoData.value = {}; return {} })
    .finally(() => { isLoading.value = false; pendingRequest = null })
  return pendingRequest
}

/* ---------- DOM helpers ---------- */
const upsertMeta = (selector, attr, name, content) => {
  if (!content) return
  let el = document.head.querySelector(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

const upsertLink = (rel, hreflang, href) => {
  const sel = hreflang
    ? `link[rel="alternate"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`
  let el = document.head.querySelector(sel)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    if (hreflang) el.setAttribute('hreflang', hreflang)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

const upsertJsonLd = (id, json) => {
  let el = document.head.querySelector(`script[type="application/ld+json"][data-id="${id}"]`)
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.dataset.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(json)
}

/* ---------- Per-page setSeo ---------- */
const setSeo = ({ title, description, image, keywords, path, locale = 'ru' }) => {
  if (title) {
    document.title = title
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title)
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title)
  }
  if (description) {
    upsertMeta('meta[name="description"]', 'name', 'description', description)
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description)
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description)
  }
  if (keywords) {
    upsertMeta('meta[name="keywords"]', 'name', 'keywords', keywords)
  }
  if (image) {
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', image)
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image)
  }

  // Canonical + hreflang
  const url = path ? `${SITE_URL}${path}` : window.location.href.split('?')[0]
  upsertLink('canonical', null, url)
  upsertMeta('meta[property="og:url"]', 'property', 'og:url', url)
  upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'website')
  upsertMeta('meta[property="og:locale"]', 'property', 'og:locale',
    locale === 'kz' ? 'kk_KZ' : locale === 'en' ? 'en_US' : 'ru_RU')
  upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image')

  // hreflang (kz/ru/en + x-default)
  const cleanPath = path || window.location.pathname
  upsertLink('alternate', 'kz', `${SITE_URL}${cleanPath}`)
  upsertLink('alternate', 'ru', `${SITE_URL}${cleanPath}`)
  upsertLink('alternate', 'en', `${SITE_URL}${cleanPath}`)
  upsertLink('alternate', 'x-default', `${SITE_URL}${cleanPath}`)
}

/* ---------- Global LocalBusiness JSON-LD ---------- */
const buildLocalBusinessSchema = (locale = 'ru') => {
  const d = seoData.value || {}
  if (!d.siteName) return null

  const sameAs = []
  if (d.whatsapp) sameAs.push(d.whatsapp)

  const addressList = (d.addresses || []).map(a => ({
    '@type': 'PostalAddress',
    addressLocality: a.city || d.cities?.[0] || '',
    streetAddress: a.address,
    addressCountry: 'KZ'
  }))

  return {
    '@context': 'https://schema.org',
    '@type': 'DrivingSchool',
    '@id': `${SITE_URL}#organization`,
    name: localized(d.siteName, locale, 'Korkem Auto'),
    alternateName: ['Korkem Auto', 'Көркем Авто'],
    url: SITE_URL,
    logo: `${SITE_URL}/logo.svg`,
    image: `${SITE_URL}/logo.svg`,
    description: localized(d.description, locale),
    email: d.email || undefined,
    telephone: d.phoneIntl || d.phone || undefined,
    address: addressList.length ? addressList : [{
      '@type': 'PostalAddress',
      addressLocality: d.cities?.[0] || 'Шымкент',
      streetAddress: localized(d.address, locale),
      addressCountry: 'KZ'
    }],
    areaServed: (d.cities || []).map(c => ({ '@type': 'City', name: c })),
    openingHoursSpecification: d.workTime ? [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '09:00',
      closes: '20:00'
    }] : undefined,
    sameAs: sameAs.length ? sameAs : undefined,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Курстары',
      itemListElement: (d.categories || []).map(c => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: `Категория ${c}`,
          serviceType: 'Driving instruction'
        }
      }))
    }
  }
}

const injectGlobalSchema = (locale = 'ru') => {
  const schema = buildLocalBusinessSchema(locale)
  if (schema) upsertJsonLd('local-business', schema)
}

/* ---------- Keyword bag (used by Home/Contact for meta keywords) ---------- */
const buildKeywords = (locale = 'ru') => {
  const d = seoData.value || {}
  const cities = d.cities || ['Шымкент']
  const addrNames = (d.addresses || []).map(a => a.address).filter(Boolean).slice(0, 5)
  const cats = d.categories || ['B', 'C', 'D']

  if (locale === 'kz') {
    return [
      ...cities.map(c => `автомектеп ${c}`),
      ...cities.map(c => `${c} автомектеп`),
      ...cats.map(c => `категория ${c}`),
      'жүргізуші куәлігі',
      'жүргізуші курстары',
      'автокөлік курстары',
      'ЖҚ ережелері тесті',
      'ПДД тесті онлайн қазақша',
      'Көркем Авто',
      ...addrNames
    ].join(', ')
  }
  return [
    ...cities.map(c => `автошкола ${c}`),
    ...cities.map(c => `автошкола в ${c}е`),
    ...cats.map(c => `категория ${c}`),
    'права на машину',
    'курсы вождения',
    'автокурсы',
    'автоинструктор',
    'ПДД онлайн тесты',
    'медсправка 073',
    'Korkem Auto',
    'Көркем Авто',
    ...addrNames
  ].join(', ')
}

export const useSeo = () => ({
  seoData,
  loading: isLoading,
  loadSeoData,
  setSeo,
  injectGlobalSchema,
  buildKeywords,
  localized
})
